/**
 * SearchResultCard Component
 * Displays individual search results with TrustScore
 */

import React from 'react';
import clsx from 'clsx';
import type { SearchResult } from '@ai360smartsearch/shared';
import { getFaviconUrl, formatRelativeTime } from '../utils/formatters';
import { getConfig } from '../utils/getConfig';
import './SearchResultCard.css';

interface SearchResultCardProps {
  result: SearchResult;
  viewMode: 'list' | 'grid' | 'card';
  onNavigate?: (url: string) => void;
  showTrustScore?: boolean;
  showTags?: boolean;
  showActions?: boolean;
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({ 
  result, 
  viewMode,
  onNavigate,
  showTrustScore: showTrustScoreProp,
  showTags: showTagsProp,
  showActions = true,
}) => {
  // Get configuration for card display
  const config = getConfig('results', {});
  
  const showTitle = config.showTitle ?? true;
  const titleMaxLength = config.titleMaxLength ?? 60;
  const showDescription = config.showDescription ?? true;
  const descriptionMaxLength = config.descriptionMaxLength ?? 150;
  const showIcon = config.showIcon ?? true;
  const showTags = showTagsProp ?? (config.showTags ?? true);
  const maxVisibleTags = config.maxVisibleTags ?? 3;
  const showUrl = config.showUrl ?? true;
  const showDate = config.showDate ?? false;
  const showAuthor = config.showAuthor ?? false;
  const showThumbnail = config.showThumbnail ?? true;
  const showVisitButton = config.showVisitButton ?? true;
  const visitButtonText = config.visitButtonText ?? 'Visitar sitio';
  const visitButtonNewTab = config.visitButtonNewTab ?? true;
  const cardBorderRadius = config.cardBorderRadius ?? 12;
  const cardShowBorder = config.cardShowBorder ?? true;
  const cardShowShadow = config.cardShowShadow ?? true;
  const showTrustScore = showTrustScoreProp ?? (config.showTrustScore ?? true);
  const trustScoreStyle = getConfig('trustScoreStyle', 'badge');

  // Hide cards without valid URL (excluding chat results which don't need URLs)
  const isChatResult = result.type === 'chat' || result.source === 'ai';
  if (!isChatResult && (!result.url || result.url === '#' || result.url === 'null' || result.url === '')) {
    return null;
  }
  
  const hasUrl = result.url && result.url !== '#';
  const faviconUrl = !isChatResult && hasUrl && showIcon ? getFaviconUrl(result.url!) : undefined;
  
  // Extract numeric trust score
  const numericTrustScore = typeof result.trustScore === 'object' 
    ? result.trustScore.totalScore 
    : result.trustScore;

  const handleClick = (e: React.MouseEvent) => {
    if (isChatResult || !hasUrl) {
      e.preventDefault();
      return;
    }
    if (onNavigate) {
      e.preventDefault();
      onNavigate(result.url!);
    }
  };
  
  const getHostname = (): string => {
    if (isChatResult || !hasUrl) return '';
    try {
      return new URL(result.url!).hostname;
    } catch {
      return result.url ?? '';
    }
  };

  // Truncate title
  const displayTitle = showTitle 
    ? (result.title && result.title.length > titleMaxLength 
        ? result.title.substring(0, titleMaxLength) + '...' 
        : result.title)
    : null;
  
  // Truncate description
  const displayDescription = showDescription
    ? ((result.content || result.snippet)?.length > descriptionMaxLength
        ? (result.content || result.snippet).substring(0, descriptionMaxLength) + '...'
        : (result.content || result.snippet))
    : null;

  return (
    <article 
      className={clsx('ai360-result-card', `ai360-result-${viewMode}`)}
      style={{ 
        borderRadius: `${cardBorderRadius}px`,
        border: cardShowBorder ? '1px solid var(--ai360-border, #e5e7eb)' : 'none',
        boxShadow: cardShowShadow ? undefined : 'none'
      }}
    >
      {/* AI Badge for AI-generated results */}
      {isChatResult && (
        <span 
          className="ai360-result-ai-badge" 
          title="Resultado generado por IA"
          aria-label="Resultado generado por inteligencia artificial"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8 2a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2H9v2h2a1 1 0 1 1 0 2H9v2a1 1 0 1 1-2 0v-2H5a1 1 0 1 1 0-2h2V7H5a1 1 0 0 1 0-2h2V3a1 1 0 0 1 1-1z"/>
          </svg>
          <span>IA</span>
        </span>
      )}

      {showThumbnail && result.thumbnail && viewMode === 'grid' && (
        <img 
          src={result.thumbnail} 
          alt={displayTitle || ''} 
          className="ai360-result-thumbnail" 
        />
      )}
      
      <div className="ai360-result-content">
        {!isChatResult && hasUrl && showUrl && (
          <div className="ai360-result-header">
            {faviconUrl && (
              <img 
                src={faviconUrl} 
                alt="" 
                className="ai360-result-favicon"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className="ai360-result-meta">
              <span className="ai360-result-url">{getHostname()}</span>
              {showDate && (result.date || result.timestamp) && (
                <span className="ai360-result-date">
                  {formatRelativeTime(
                    result.date || 
                    (result.timestamp ? result.timestamp.toString() : new Date().toISOString())
                  )}
                </span>
              )}
              {showAuthor && result.author && (
                <span className="ai360-result-author">por {result.author}</span>
              )}
            </div>
          </div>
        )}

        {displayTitle && (
          <h3 className="ai360-result-title">
            {hasUrl ? (
              <a
                href={result.url!}
                onClick={handleClick}
                target={visitButtonNewTab ? "_blank" : "_self"}
                rel="noopener noreferrer"
              >
                {displayTitle}
              </a>
            ) : (
              displayTitle
            )}
          </h3>
        )}
        
        {displayDescription && (
          <p className="ai360-result-excerpt">{displayDescription}</p>
        )}
        
        {showTags && result.tags && result.tags.length > 0 && (
          <div className="ai360-result-tags">
            {result.tags.slice(0, maxVisibleTags).map((tag, index) => (
              <span key={index} className="ai360-result-tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        {showTrustScore && numericTrustScore !== undefined && numericTrustScore > 0 && (
          <div className="ai360-trust-score">
            <span className="ai360-trust-label">TrustScore:</span>
            <span className="ai360-trust-value">{Math.round(numericTrustScore)}%</span>
            <div className="ai360-trust-bar">
              <div 
                className="ai360-trust-fill" 
                style={{ 
                  width: `${numericTrustScore}%`,
                  backgroundColor: getTrustScoreColor(numericTrustScore)
                }}
              />
            </div>
          </div>
        )}

        {showActions && showVisitButton && hasUrl && (
          <div className="ai360-result-actions">
            <a
              href={result.url!}
              onClick={handleClick}
              target={visitButtonNewTab ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="ai360-result-visit-button"
            >
              {visitButtonText}
            </a>
          </div>
        )}
      </div>
    </article>
  );
};

function getTrustScoreColor(score: number): string {
  if (score >= 80) return '#10b981'; // green
  if (score >= 60) return '#f59e0b'; // amber
  return '#ef4444'; // red
}
